import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from "@mui/icons-material/Description";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, IconButton } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import axios from 'axios';
import { AnimatePresence, motion } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Pause, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from "react";
import usePermissions from "../../../../Hooks/usePermissions";
import { Form, Modal } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { GeoJSON, LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";
import "./FlashDensity.css";
import { captureAndGeneratePdf } from "../OpenmeteoJMA/ReportGenerator_FD";
import {
  characteristicColors,
  riskTranslations,
  translations,
} from "../OpenmeteoJMA/Translations_Lightning";

// eslint-disable-next-line
import ImpactedAreaLN from '../OpenmeteoJMA/ImpactedArea_LN';
import InformationLN from '../OpenmeteoJMA/Information_LN';
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Initial view state
const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

// --- DATA UTILS ---
const parseDateHr = (dateHr) => {
  if (!dateHr) return new Date();
  const [datePart, hrPart] = dateHr.split('_');
  const year = parseInt(datePart.slice(0, 4));
  const month = parseInt(datePart.slice(4, 6)) - 1;
  const day = parseInt(datePart.slice(6, 8));
  const hour = parseInt(hrPart.slice(0, 2));
  return new Date(year, month, day, hour);
};

const getValidityRange = (targetDateStr) => {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  if (targetDateStr === todayStr) {
    const curHour = String(now.getUTCHours()).padStart(2, '0');
    return `[UTC00-${curHour}]`;
  }
  return `[UTC00-24]`;
};

const formatTimestamp = (dateHr) => {
  if (!dateHr) return "--";
  let isoDate = "";
  let hourStr = "00";
  
  if (dateHr.includes('_')) {
    const parts = dateHr.split('_');
    isoDate = parts[0];
    hourStr = (parts[1] && parts[1].length >= 2) ? parts[1].slice(0, 2) : "00";
  } else {
    isoDate = dateHr;
  }
  
  if (isoDate.length === 8 && !isoDate.includes('-')) {
    isoDate = `${isoDate.slice(0, 4)}-${isoDate.slice(4, 6)}-${isoDate.slice(6, 8)}`;
  }

  const rangeStr = getValidityRange(isoDate);
  return `${isoDate} ${hourStr}:00${rangeStr}`;
};

// --- COMPONENTS ---
const ZoomToFeature = ({ feature }) => {
  const map = useMap();
  useEffect(() => {
    if (feature && feature.features && feature.features[0]) {
      const geojsonLayer = L.geoJSON(feature);
      map.flyToBounds(geojsonLayer.getBounds(), { padding: [20, 20] });
    }
  }, [feature, map]);
  return null;
};

// Boundary styles (same as FireRisk)
const getBoundaryStyle = () => ({
  color: "black",
  weight: 1,
  fillOpacity: 0,
});

const selectedGeojsonStyle = (feature, selectedSuco, selectedSubMunicipality, selectedMunicipality) => {
  if (feature.properties.ADM_Code) {
    if (feature.properties.ADM_Code === selectedSuco) {
      return { color: "#333333", weight: 3, fillOpacity: 0 };
    }
  }
  if (feature.properties.ADM2_Code || feature.properties.ADM_Code) {
    const code = feature.properties.ADM2_Code || feature.properties.ADM_Code;
    if (code === selectedSubMunicipality) {
      return { color: "#333333", weight: 3, fillOpacity: 0 };
    }
  }
  if (feature.properties.ADM1_Code || feature.properties.ADM_Code) {
    const code = feature.properties.ADM1_Code || feature.properties.ADM_Code;
    if (code === selectedMunicipality) {
      return { color: "#333333", weight: 3, fillOpacity: 0 };
    }
  }
  return getBoundaryStyle();
};

const riskColors = {
  'High': 'rgb(230, 45, 58)',
  'Medium': 'rgb(243, 155, 58)',
  'Low': 'rgb(243, 235, 18)',
  'NoRisk': 'rgb(148, 203, 85)',
  'N/A': 'rgba(222, 222, 223, 0.6)'
};

const BoundaryLayer = ({ boundaryData, apiData, currentDateHr, showAllTooltips }) => {
  if (!boundaryData) return null;

  const getStyle = (feature) => {
    const admcode = feature.properties.ADM_Code;
    const entry = apiData.find(d => d.admcode === admcode && d.date_hr === currentDateHr);

    let fillColor = riskColors['N/A'];
    let fillOpacity = 0.6;

    if (entry) {
      let advisory = entry.advisory || 'N/A';
      const matchRisk = (text) => {
        const t = text.toLowerCase();
        if (t.includes('high')) return 'High';
        if (t.includes('medium')) return 'Medium';
        if (t.includes('low')) return 'Low';
        if (t.includes('norisk') || t.includes('no risk')) return 'NoRisk';
        return 'N/A';
      };

      const riskLevel = matchRisk(advisory);
      if (riskColors[riskLevel]) {
        fillColor = riskColors[riskLevel];
        fillOpacity = 0.8;
      }
    }

    return {
      fillColor,
      fillOpacity,
      color: 'white',
      weight: 1,
      opacity: 1
    };
  };

  return (
    <GeoJSON
      key={`lightning-${currentDateHr}-${showAllTooltips}`}
      data={boundaryData}
      style={getStyle}
      onEachFeature={(feature, layer) => {
        const admcode = feature.properties.ADM_Code;
        const entry = apiData.find(d => d.admcode === admcode && d.date_hr === currentDateHr);

        const subMunicipality = feature.properties.ADM2 || 'Unknown';
        const advisory = entry ? (entry.advisory || 'N/A') : 'N/A';
        const density = entry ? entry.lightningflashdensity : 'N/A';

        const matchRiskLocal = (text) => {
          const t = text.toLowerCase();
          if (t.includes('high')) return 'High';
          if (t.includes('medium')) return 'Medium';
          if (t.includes('low')) return 'Low';
          if (t.includes('norisk') || t.includes('no risk')) return 'NoRisk';
          return 'N/A';
        };

        const riskLevel = matchRiskLocal(advisory);
        const riskColor = riskColors[riskLevel] || 'black';

        let tooltipContent = `
          <div style="font-size: 11px; line-height: 1.4; min-width: 140px;">
            <div style="font-weight: 700; color: #1565c0; margin-bottom: 3px; border-bottom: 1px solid #e0e0e0; padding-bottom: 3px;">
              ${subMunicipality}
            </div>
            <div><strong>LFD:</strong> ${density}</div>
            <div><strong>Advisory:</strong> <span style="font-weight: 700; color: ${riskColor};">${riskLevel}</span></div>
          </div>`;

        layer.bindTooltip(tooltipContent, {
          sticky: !showAllTooltips,
          permanent: showAllTooltips,
          direction: 'top',
          offset: [0, -10]
        });
      }}
    />
  );
};

const FlashDensity = () => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);

  // Data States
  const [apiData, setApiData] = useState([]);
  const [adm1Data, setAdm1Data] = useState(null);
  const [adm2Data, setAdm2Data] = useState(null);
  const [adm3Data, setAdm3Data] = useState(null);

  // Filter Data States
  const [municipalities, setMunicipalities] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [sucos, setSucos] = useState([]);

  // Selection States
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState("");
  const [selectedSuco, setSelectedSuco] = useState("");
  const [zoomFeature, setZoomFeature] = useState(null);
  const [initialBounds, setInitialBounds] = useState(null);

  // Time Control States
  const [uniqueDateHrs, setUniqueDateHrs] = useState([]);
  const [filteredDateHrs, setFilteredDateHrs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Date Filter State
  const [, setAvailableDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState("");

  // UI States
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [, setPdfLanguage] = useState("en");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showAllTooltips, setShowAllTooltips] = useState(false);

  // Map Ref
  const mapRef = useRef(null);

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          adm1Res,
          adm2Res,
          adm3Res,
          apiRes,
          munisRes,
          subMunisRes,
          sucosRes
        ] = await Promise.all([
          fetch('/DATA/OSMbased_adm1_boundary.json').then(r => r.json()),
          fetch('/DATA/OSMbased_adm2_boundary.json').then(r => r.json()),
          fetch('/DATA/OSMbased_adm3_boundary.json').then(r => r.json()),
          axios.get(`${backendUrl}lightning/lightning-flash-density/`),
          axios.post(`${backendUrl}Municipalities/municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/suco_list/`)
        ]);

        setAdm1Data(adm1Res);
        setAdm2Data(adm2Res);
        setAdm3Data(adm3Res);

        if (adm1Res && adm1Res.features) {
          const geojsonLayer = L.geoJSON(adm1Res);
          setInitialBounds(geojsonLayer.getBounds());
          // setMapBounds(geojsonLayer.getBounds());
        }

        const data = apiRes.data;
        // Merge functionality: append without overriding if already exists
        setApiData(prev => {
          const newDataMap = new Map();
          prev.forEach(d => newDataMap.set(d.admcode + '_' + d.date_hr, d));
          data.forEach(d => newDataMap.set(d.admcode + '_' + d.date_hr, d));
          const merged = Array.from(newDataMap.values());
          return merged.sort((a, b) => parseDateHr(a.date_hr) - parseDateHr(b.date_hr));
        });

        const sortedData = data.sort((a, b) => parseDateHr(a.date_hr) - parseDateHr(b.date_hr));

        const dateHrSet = new Set(sortedData.map(d => d.date_hr));
        const sortedDateHrs = Array.from(dateHrSet).sort((a, b) => {
          return parseDateHr(a) - parseDateHr(b);
        });
        setUniqueDateHrs(prev => {
          const newSet = new Set([...prev, ...sortedDateHrs]);
          return Array.from(newSet).sort((a, b) => parseDateHr(a) - parseDateHr(b));
        });

        const days = new Set(sortedData.map(d => d.date));
        const sortedDays = Array.from(days).sort((a, b) => {
          const toDate = (s) => {
            if (!s || s.length !== 8) return new Date();
            return new Date(`${s.slice(4, 8)}-${s.slice(2, 4)}-${s.slice(0, 2)}`);
          };
          return toDate(a) - toDate(b);
        });
        setAvailableDays(prev => {
          const newSet = new Set([...prev, ...sortedDays]);
          return Array.from(newSet).sort((a, b) => {
            const toDate = (s) => {
              if (!s || s.length !== 8) return new Date();
              return new Date(`${s.slice(4, 8)}-${s.slice(2, 4)}-${s.slice(0, 2)}`);
            };
            return toDate(a) - toDate(b);
          });
        });

        if (sortedDays.length > 0) {
          setSelectedDay(prev => prev || sortedDays[0]);
        }

        setMunicipalities(munisRes.data);
        setSubMunicipalities(subMunisRes.data);
        setSucos(sucosRes.data);

      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- ZOOM LOGIC ---
  useEffect(() => {
    let featureToZoom = null;
    if (selectedSuco && adm3Data) {
      featureToZoom = adm3Data.features.find((f) => f.properties.ADM_Code === selectedSuco);
    } else if (selectedSubMunicipality && adm2Data) {
      featureToZoom = adm2Data.features.find((f) => f.properties.ADM_Code === selectedSubMunicipality);
    } else if (selectedMunicipality && adm1Data) {
      featureToZoom = adm1Data.features.find((f) => f.properties.ADM_Code === selectedMunicipality);
    }
    setZoomFeature(featureToZoom);
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, adm1Data, adm2Data, adm3Data]);

  // --- FILTER LOGIC ---
  useEffect(() => {
    let targetList = [];
    if (selectedDay) {
      targetList = uniqueDateHrs.filter(dh => {
        const [datePart] = dh.split('_');
        const y = datePart.slice(0, 4);
        const m = datePart.slice(4, 6);
        const d = datePart.slice(6, 8);
        return `${d}${m}${y}` === selectedDay;
      });
      setFilteredDateHrs(targetList);
    } else {
      targetList = uniqueDateHrs;
      setFilteredDateHrs(targetList);
    }

    if (targetList.length > 0) {
      const now = new Date();
      let closestIndex = 0;
      let minDiff = Infinity;
      targetList.forEach((dh, idx) => {
        const diff = Math.abs(parseDateHr(dh).getTime() - now.getTime());
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = idx;
        }
      });
      setCurrentIndex(closestIndex);
    } else {
      setCurrentIndex(0);
    }
  }, [selectedDay, uniqueDateHrs]);

  // const filteredSubMunis = useMemo(() => {
  //   if (!selectedMunicipality) return [];
  //   return subMunicipalities.filter(s => s.adm1_code === selectedMunicipality);
  // }, [selectedMunicipality, subMunicipalities]);

  const filteredSucos = useMemo(() => {
    if (!selectedSubMunicipality) return [];
    return sucos.filter(s => s.adm2_code === selectedSubMunicipality);
  }, [selectedSubMunicipality, sucos]);

  const currentTimestamp = filteredDateHrs[currentIndex] || "";

  const impactedTableData = useMemo(() => {
    if (!apiData || !filteredDateHrs.length) return [];

    let data = apiData.filter(d => filteredDateHrs.includes(d.date_hr));

    if (selectedMunicipality) {
      data = data.filter(d => d.admcode.startsWith(selectedMunicipality));
    }
    if (selectedSubMunicipality) {
      data = data.filter(d => d.admcode === selectedSubMunicipality || d.admcode.startsWith(selectedSubMunicipality));
    }

    return data.map(item => {
      const muniCode = item.admcode.substring(0, 4);
      const subCode = item.admcode.substring(0, 6);

      const muni = municipalities.find(m => m.adm1_code === muniCode);
      const sub = subMunicipalities.find(s => s.adm2_code === subCode);

      // Look up socio-economic data from adm2Data GeoJSON
      let socioData = {};
      if (adm2Data && adm2Data.features) {
        const geoFeature = adm2Data.features.find(f => f.properties.ADM_Code === item.admcode);
        if (geoFeature) {
          socioData = {
            Population: geoFeature.properties.Population || 0,
            Male: geoFeature.properties.Male || 0,
            Female: geoFeature.properties.Female || 0,
            Children: geoFeature.properties.HH_Urban || 0,
            Elderly: geoFeature.properties.HH_Rural || 0,
            Household: geoFeature.properties.Household || 0,
          };
        }
      }

      return {
        ...item,
        Municipality: muni ? muni.adm1 : muniCode,
        PostAdmin: sub ? sub.adm2 : subCode,
        ...socioData,
      };
    });
  }, [apiData, filteredDateHrs, selectedMunicipality, selectedSubMunicipality, municipalities, subMunicipalities, adm2Data]);

  // --- HANDLERS ---
  const handleGenerateReportClick = () => setShowLanguageModal(true);

  const handleLanguageSelectAndGenerate = async (selectedLanguage) => {
    setShowFilterPanel(false);
    setPdfLanguage(selectedLanguage);
    setShowLanguageModal(false);
    await captureAndGeneratePdf({
      moduleName: "FlashDensity",
      language: selectedLanguage,
      mapRef,
      setIsGeneratingReport,
      currentTimestamp,
      impactedTableData,
      translations,
      riskTranslations,
      characteristicColors,
    });
  };

  const handleReset = () => {
    setSelectedMunicipality("");
    setSelectedSubMunicipality("");
    setSelectedSuco("");
    setZoomFeature(null);
    if (initialBounds) {
      // This is a bit tricky with react-leaflet. 
      // One way is to trigger a dummy feature zoom to the whole country.
      setZoomFeature({ type: 'FeatureCollection', features: adm1Data.features });
    }
  };

  const handlePlayPause = () => setIsPlaying(!isPlaying);

  const handleSliderClick = (e) => {
    if (!filteredDateHrs.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const index = Math.round(percentage * (filteredDateHrs.length - 1));
    setCurrentIndex(Math.max(0, Math.min(index, filteredDateHrs.length - 1)));
    setIsPlaying(false);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && filteredDateHrs.length > 0) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % filteredDateHrs.length);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, filteredDateHrs.length]);

  return (
    <div className="fm_flash-density-map-container" style={{ marginBottom: '20px' }}>
      <Box sx={{ position: "relative" }}>
        {hasPermission('lightning_flash_density_generate_report') && (
        <div className="lighting-flash-density section-buttons-container" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            className="generate-report-btn"
            onClick={handleGenerateReportClick}
            disabled={loading || isGeneratingReport}
          >
            {loading || isGeneratingReport ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              <DescriptionIcon style={{ fontSize: '18px' }} />
            )}
            Generate Report
          </button>
        </div>
        )}

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilterPanel && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ type: "spring", stiffness: 300 }}
              style={{ position: "absolute", top: 10, left: 10, zIndex: 1001 }}
            >
              <div className="FilterPanel">
                <div className="FilterPanel-container">
                  <div className="location-filter-container">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <h4 className="dropdown-heading" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#333' }}>Location:</h4>
                      <IconButton onClick={handleReset} size="small"
                        sx={{
                          backgroundColor: 'white',
                          padding: '5px',
                          borderRadius: '50%',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                      >
                        <RestartAltIcon sx={{ color: "#0d6efd", fontSize: 20 }} />
                      </IconButton>
                    </Box>

                    <div className="dropdowns-row">
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '12px', color: '#666' }}>Municipality:</Form.Label>
                        <Form.Select size="sm" value={selectedMunicipality} onChange={(e) => { setSelectedMunicipality(e.target.value); setSelectedSubMunicipality(""); setSelectedSuco(""); }}>
                          <option value="">Select Municipality</option>
                          {municipalities.map((m, i) => <option key={i} value={m.adm1_code}>{m.adm1}</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '12px', color: '#666' }}>Suco:</Form.Label>
                        <Form.Select size="sm" value={selectedSuco} onChange={(e) => setSelectedSuco(e.target.value)} disabled={!selectedSubMunicipality}>
                          <option value="">Select Suco</option>
                          {filteredSucos.map((m, i) => <option key={i} value={m.adm3_code}>{m.adm3}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Map and Overlays */}
        <div className="map-container-lightning">
          <div className="map-wrapper">
            <MapContainer
              ref={mapRef}
              center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
              zoom={INITIAL_VIEW_STATE.zoom}
              attributionControl={false}
              className="mapcontaineralllightning"
            >
              <MapHomeControl />
              <LayersControl position="topright">
                <LayersControl.BaseLayer name="Light Map">
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer checked name="OpenStreetMap">
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                </LayersControl.BaseLayer>
                {adm1Data && (
                  <LayersControl.Overlay name="Municipality">
                    <GeoJSON data={adm1Data} style={(feature) => selectedGeojsonStyle(feature, selectedSuco, selectedSubMunicipality, selectedMunicipality)} />
                  </LayersControl.Overlay>
                )}
                {adm2Data && filteredDateHrs.length > 0 && (
                  <LayersControl.Overlay checked name="Lightning Risk">
                    <BoundaryLayer
                      boundaryData={adm2Data}
                      apiData={apiData}
                      currentDateHr={currentTimestamp}
                      showAllTooltips={showAllTooltips}
                    />
                  </LayersControl.Overlay>
                )}
              </LayersControl>
              {zoomFeature && <ZoomToFeature feature={zoomFeature.features ? zoomFeature : { type: 'FeatureCollection', features: [zoomFeature] }} />}

              {/* Information */}
              <div className="info-control-lightning">
                <InformationLN />
              </div>

              {/* Show All Tooltips Control - to the right of the info "i" icon */}
              <div
                className="show-tooltips-control"
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "56px",
                  zIndex: 1000,
                }}
              >
                <button
                  onClick={() => setShowAllTooltips(prev => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    background: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px) brightness(1.1)",
                    WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                    color: "black",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                    transition: "all 0.2s ease",
                    letterSpacing: "0.3px",
                  }}
                  title={!showAllTooltips ? "Hide all labels" : "Show all labels"}
                >
                  {!showAllTooltips ? <CloseIcon style={{ fontSize: 16 }} /> : <CheckIcon style={{ fontSize: 16 }} />}
                  {!showAllTooltips ? "Hide All Labels" : "Show All Labels"}
                </button>
              </div>

              {/* Legend */}
              <MapLegendControl
                position="bottomright"
                sections={[
                  {
                    items: [
                      { label: "High", color: "rgb(230, 45, 58)" },
                      { label: "Medium", color: "rgb(243, 155, 58)" },
                      { label: "Low", color: "rgb(243, 235, 18)" },
                      { label: "No-Risk", color: "rgb(148, 203, 85)" },
                      { label: "N/A", color: "rgb(222, 222, 223)" },
                    ],
                  },
                ]}
              />
            </MapContainer>

            {/* Time Slider Overlay - Moved outside map and restyled */}
            <div
              className="dashboardslider fm_slider-container"
              style={{
                backgroundColor: 'white',
                borderTop: '1px solid #e5e7eb',
                padding: '0.5rem 1rem',
                zIndex: 100,
                borderRadius: '0 0 1rem 1rem',
                marginTop: '0rem' // To overlap slightly with the map's rounded bottom if needed, or just let it be flush
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button
                  onClick={handlePlayPause}
                  style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    borderRadius: '9999px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s',
                    border: 'none',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: '2px' }} />}
                </button>

                <div style={{ flex: 1 }}>
                  <div style={{ position: 'relative', marginTop: '10px' }}>
                    <div
                      style={{
                        width: '100%',
                        height: '0.6rem',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '9999px',
                        cursor: filteredDateHrs.length ? 'pointer' : 'default',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onClick={handleSliderClick}
                    >
                      {filteredDateHrs.length > 0 && (
                        <>
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              backgroundColor: '#2563eb',
                              borderRadius: currentIndex === 0 ? '9999px' : '9999px 0 0 9999px',
                              width: `${(currentIndex / (filteredDateHrs.length - 1)) * 100}%`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: `calc(${(currentIndex / (filteredDateHrs.length - 1)) * 100}%)`,
                              transform: 'translate(-50%, -50%)',
                              width: '1.2rem',
                              height: '1.2rem',
                              backgroundColor: '#2563eb',
                              borderRadius: '50%',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                              zIndex: 2,
                              transition: 'left 0.3s ease',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0 0.25rem',
                            }}
                          >
                            {filteredDateHrs.map((_, index) => (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    width: '1px',
                                    height: '0.6rem',
                                    backgroundColor: index <= currentIndex ? 'rgba(255,255,255,0.5)' : '#9ca3af',
                                    transition: 'background-color 0.2s',
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '0.5rem',
                        padding: '0 0.25rem',
                      }}
                    >
                      {filteredDateHrs.map((dh, index) => {
                        const shouldShowLabel = filteredDateHrs.length <= 12 ||
                          index === 0 ||
                          index === filteredDateHrs.length - 1 ||
                          index % 4 === 0;
                        const timeLabel = dh.split('_')[1]?.slice(0, 2) + ':00';

                        return (
                          <span
                            key={index}
                            style={{
                              fontSize: '0.65rem',
                              transition: 'color 0.2s',
                              color: index === currentIndex ? '#2563eb' : '#6b7280',
                              fontWeight: index === currentIndex ? 600 : 400,
                              textAlign: 'center',
                              width: '20px',
                              visibility: shouldShowLabel ? 'visible' : 'hidden'
                            }}
                          >
                            {timeLabel}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', marginTop: '5px', fontSize: '13px', color: '#2563eb', fontWeight: 'bold' }}>
                {currentTimestamp ? formatTimestamp(currentTimestamp) : 'Loading...'}
              </div>
            </div>
          </div>

          {/* Tab Interface - REMOVED SECOND TAB AS REQUESTED */}
          <div className="section-buttons-container">
            <button className={`section-button active`} style={{ cursor: 'default' }}>
              Impacted Area
            </button>
          </div>

          <div className="section-content-container">
            <ImpactedAreaLN
              loading={loading}
              tableData={impactedTableData}
              currentTimestamp={currentTimestamp ? formatTimestamp(currentTimestamp) : ''}
              headerDate={currentTimestamp ? formatTimestamp(currentTimestamp) : 'Loading...'}
            />
          </div>
        </div>
      </Box>

      {/* Language Modal */}
      <Modal
        show={showLanguageModal}
        onHide={() => setShowLanguageModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Body className="language-modal-body">
          <div className="language-modal-container">
            {/* Icon */}
            <div className="language-modal-icon">
              🌐
            </div>

            <h3 className="language-modal-title">
              Select Report Language
            </h3>
            <p className="language-modal-description">
              Choose the language for the generated advisory PDF report.
            </p>

            <div className="language-modal-cards">
              {/* English Card */}
              <div
                onClick={() => handleLanguageSelectAndGenerate("en")}
                className="language-card-option english"
              >
                <span className="card-emoji">🇬🇧</span>
                <span className="card-label">English</span>
              </div>

              {/* Tetum Card */}
              <div
                onClick={() => handleLanguageSelectAndGenerate("tetum")}
                className="language-card-option tetum"
              >
                <span className="card-emoji">🇹🇱</span>
                <span className="card-label">Tetum</span>
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => setShowLanguageModal(false)}
              className="language-modal-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FlashDensity;
